import type { SentimentHeatmapDay, SentimentHeatmapSummary } from '@lensora/shared'
import { cn } from '@lensora/ui'
import * as React from 'react'

// Sequential data-viz gradient (red → green) — a colour scale, not a UI token.
const GRADIENT_STOPS = ['#FCEBEB', '#F09595', '#FAC775', '#97C459', '#1D9E75']

function scoreToColor(score: number): string {
  if (score >= 80) return '#1D9E75'
  if (score >= 65) return '#97C459'
  if (score >= 50) return '#FAC775'
  if (score >= 35) return '#F09595'
  return '#FCEBEB'
}

function monthLabels(days: SentimentHeatmapDay[]): string[] {
  const labels: string[] = []
  let lastMonth = ''
  for (const day of days) {
    const month = new Date(day.date).toLocaleDateString('en-GB', { month: 'short' })
    if (month !== lastMonth) {
      labels.push(month)
      lastMonth = month
    }
  }
  return labels
}

interface SentimentHeatmapProps {
  data: SentimentHeatmapSummary | null
  isLoading: boolean
}

export function SentimentHeatmap({ data, isLoading }: SentimentHeatmapProps) {
  const [tooltip, setTooltip] = React.useState<{ x: number; y: number; day: SentimentHeatmapDay } | null>(null)

  const days = data?.days ?? []
  const overall = data?.overall ?? null
  const score = overall ?? 0
  const scoreColor = scoreToColor(score)

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-border bg-surface p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
            Sentiment over time
          </p>
          <p className="text-[11px] text-text-muted">How AI models talk about your brand</p>
        </div>
        {overall !== null && (
          <div className="text-right">
            <p className="mb-0.5 text-[10px] text-text-muted">Your sentiment</p>
            <span className="text-[28px] font-bold leading-none" style={{ color: scoreColor }}>
              {overall}
            </span>
          </div>
        )}
      </div>

      {overall !== null && (
        <div>
          <div className="mb-1.5 flex justify-between">
            <span className="text-[10px] text-text-muted">0%</span>
            <span className="text-[10px] text-text-muted">Average</span>
            <span className="text-[10px] text-text-muted">100%</span>
          </div>
          <div
            className="relative mb-1 h-2.5 rounded-full"
            style={{ background: `linear-gradient(to right, #E24B4A, ${GRADIENT_STOPS.join(', ')})` }}
          >
            <div
              className="absolute top-1/2 h-5 w-1 -translate-x-1/2 -translate-y-1/2 rounded-sm bg-text"
              style={{ left: `${score}%`, boxShadow: '0 0 0 2px var(--surface)' }}
            />
          </div>
          <p className="mt-2 text-[11px] text-text-muted">Sentiment over the last 6 months</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex h-[60px] items-center justify-center">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-brand-accent" />
        </div>
      ) : days.length === 0 ? (
        <p className="text-xs text-text-muted">No sentiment data yet — run some prompts to start tracking.</p>
      ) : (
        <>
          <div className="mb-1 flex justify-between">
            {monthLabels(days).map((m, i) => (
              <span key={`${m}-${i}`} className="text-[10px] text-text-muted">
                {m}
              </span>
            ))}
          </div>
          <div className="relative flex flex-wrap gap-0.5">
            {days.map((day, i) => (
              <div
                key={i}
                onMouseEnter={(e) => {
                  const cell = e.currentTarget
                  const container = cell.parentElement
                  if (!container) return
                  const cellRect = cell.getBoundingClientRect()
                  const containerRect = container.getBoundingClientRect()
                  setTooltip({ x: cellRect.left - containerRect.left, y: cellRect.top - containerRect.top, day })
                }}
                onMouseLeave={() => setTooltip(null)}
                className={cn(
                  'h-[11px] w-[11px] shrink-0 rounded-sm transition-transform hover:scale-125',
                  day.count === 0 && 'bg-border'
                )}
                style={day.count > 0 ? { background: scoreToColor(day.score) } : undefined}
              />
            ))}
            {tooltip && (
              <div
                className="pointer-events-none absolute z-50 whitespace-nowrap rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text shadow-lg"
                style={{ left: tooltip.x - 10, top: tooltip.y - 64 }}
              >
                <strong>
                  {new Date(tooltip.day.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </strong>
                <br />
                Sentiment:{' '}
                <strong style={{ color: scoreToColor(tooltip.day.score) }}>{tooltip.day.score}</strong>
                {tooltip.day.count > 0 && (
                  <>
                    <br />
                    <span className="text-[11px] text-text-muted">
                      {tooltip.day.count} scan{tooltip.day.count !== 1 ? 's' : ''}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            <span className="text-[10px] text-text-muted">Low</span>
            {GRADIENT_STOPS.map((c) => (
              <div key={c} className="h-2.5 w-2.5 rounded-sm" style={{ background: c }} />
            ))}
            <span className="text-[10px] text-text-muted">High</span>
          </div>
        </>
      )}
    </div>
  )
}
